/**
 * Google Tag Manager loader. Injects the standard GTM snippet at app boot using
 * the active brand's container id (`brand.gtmContainerId`). Runtime injection
 * (rather than a static tag in the HTML template) keeps GTM per-brand: the id
 * lives in the brand config, so each whitelabel build loads its own container
 * — or none, when the brand sets no id.
 *
 * Design: never let tag loading break app boot. No-op when the brand has no
 * container id; a failed DOM insert is swallowed. The googletagmanager.com
 * origin is already allowed by the production CSP (`rspack.config.ts`).
 *
 * The <noscript> iframe fallback from Google's copy-paste snippet is omitted on
 * purpose: this is a JS-only SPA that renders nothing without JavaScript, so a
 * no-JS tracking fallback can never fire.
 */

import { brand } from '@/config/brand';

declare global {
  interface Window {
    dataLayer?: unknown[];
  }
}

let injected = false;

/**
 * Load GTM for the active brand. Idempotent — repeat calls after the first are
 * no-ops. Safe to call at boot next to `initAnalytics()`; does nothing when the
 * brand defines no `gtmContainerId`.
 */
export function initGtm(): void {
  if (injected) return;
  const containerId = brand.gtmContainerId?.trim();
  if (!containerId) return;
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  try {
    // Standard GTM bootstrap: seed the dataLayer with the gtm.start event, then
    // append the async gtm.js loader before the first <script>.
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ 'gtm.start': Date.now(), event: 'gtm.js' });

    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(containerId)}`;

    const first = document.getElementsByTagName('script')[0];
    if (first?.parentNode) {
      first.parentNode.insertBefore(script, first);
    } else {
      (document.head || document.documentElement).appendChild(script);
    }
    injected = true;
  } catch {
    // swallow — tag loading must never crash the app
  }
}
